import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { DeliveryWindowOutputRecord } from '@navio/nsdk';
import { invalidateQueryExactMatch, noddiAsync, URLKeys } from 'noddi-async';
import { NoddiButton, NoddiDialog, NoddiInfoBanner } from 'noddi-ui-web';
import { BookingDetailsTabs, customerRoutes, getBookingTimeWindowDisplayText } from 'noddi-util';
import { useNavigate } from 'react-router-dom';

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  bookingSlug: string;
  bookingId: number;
  selectedTimeWindow: DeliveryWindowOutputRecord | undefined;
};

export const EditBookingTimeWindowDialog = ({
  isOpen,
  setIsOpen,
  bookingSlug,
  bookingId,
  selectedTimeWindow
}: Props) => {
  const navigate = useNavigate();

  const {
    mutateAsync: updateBooking,
    isPending: isUpdateBookingPending,
    isError: isUpdateBookingError
  } = noddiAsync.usePatch({
    type: URLKeys.patchBooking,
    queryConfig: {
      onSuccess: async () => {
        await invalidateQueryExactMatch({
          urlKey: URLKeys.getBookingInfo,
          input: { slug: bookingSlug }
        });
        navigate(customerRoutes.bookingInfo.getPath({ slug: bookingSlug, tab: BookingDetailsTabs.ORDER_DETAILS }));
      }
    }
  });

  if (!selectedTimeWindow) {
    return null;
  }

  return (
    <NoddiDialog
      title={t`Change time?`}
      open={isOpen}
      onClose={() => setIsOpen(false)}
      footer={
        <div className='flex flex-col gap-4'>
          {isUpdateBookingError && (
            <NoddiInfoBanner
              variant='error'
              texts={[{ value: t`We had trouble updating your booking. Please try again.` }]}
            />
          )}
          <div className='flex justify-end gap-2'>
            <NoddiButton
              // oxlint-disable-next-line typescript/no-misused-promises -- async onClick runs updateBooking(); React discards the returned promise, so a rejection is unhandled unless the body catches
              onClick={async () =>
                await updateBooking({
                  bookingId,
                  deliveryWindow: {
                    id: selectedTimeWindow.deliveryWindowId,
                    endsAt: selectedTimeWindow.endsAt,
                    startsAt: selectedTimeWindow.startsAt
                  }
                })
              }
              loading={isUpdateBookingPending}
            >
              <Trans>Change time</Trans>
            </NoddiButton>
          </div>
        </div>
      }
    >
      <div className='flex flex-col gap-2'>
        <p>
          <Trans>You have selected</Trans>
          <b> {getBookingTimeWindowDisplayText({ timeWindow: selectedTimeWindow })}</b>
        </p>
        <p>
          <Trans>
            Changing the booking time is free. Rescheduling is available until 18:00 the day before the booking date.
          </Trans>
        </p>
      </div>
    </NoddiDialog>
  );
};
